// スポット通常入札の「全約定 / 部分約定」 判定。
//
// spec AC-BSP-047 はステータス列に 全約定 と 部分約定 を別区分として要求するが、
// proto `BiddingSpotBidStatus` は 5 値 (未指定 / 約定待ち / 約定 / 不約定 / 失敗)
// で、 その 2 つを区別しない。 表示側で導出しているのはそのため。
//
// **区別が付かないのは上流に情報が無いからではない**。 eSLJ day_ahead の
// `BidStatus` は `BID_STATUS_CONTRACTED` (全約定) と
// `BID_STATUS_PARTIAL_CONTRACTED` (部分約定) を別値で返しており、 取引所側の
// 約定区分は権威的に届いている。 それを 1 値へ潰しているのは ebalance の ACL
// (`go/internal/backend/infrastructure/eslj/bid_match_gateway.go`
// `spotBidStatusFromProto`) で、 domain VO `SpotBidStatus` に部分約定を表す値が
// 無いためである。 本関数の 約定量 < 入札量 は、 その潰された区分を表示層で
// 再構成する **代理判定** であって取引所の一次情報ではない。
//
// 代理判定である以上ズレ得る点を残しておく: 同 ACL は約定量を 0.1 MW grid へ
// `Truncate(1)` してから保存する。 入札量は元から 0.1 MW 刻み (`numeric(10,1)`)
// なので全約定行では両者が一致するが、 取引所が CONTRACTED を返しつつ約定量だけ
// grid 外の値で返した場合は「部分約定」 と表示され得る。 恒久解は権威値
// (CONTRACTED / PARTIAL_CONTRACTED) を domain VO → proto で運ぶこと。
//
// 時間前入札は proto に PARTIAL_MATCHED / ALL_MATCHED を持つので導出が要らず、
// ブロック入札は all-or-nothing で部分約定がそもそも存在しない (AC-BSP-049)
// — 代理判定が要るのはスポット通常入札だけ。
//
// 画面 (SpotBidTable) と CSV (spotBidCsv) が同じ区分を出すよう、 数量比較はこの
// 1 関数に閉じる。
import { BiddingSpotBidStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'
import type { SpotBidStatusCategory } from '~/pages/SpotBidding/models/spotBidStatusCategory'

/** 判定に要る field だけを受ける (= 行全体を組み立てずに単体テストできる)。 */
export type SpotBidMatchQuantities = Pick<
  SpotBidRow,
  'bidStatus' | 'volumeMwh' | 'execVolumeMwh'
>

/**
 * 部分約定 (= 約定したが 約定量 < 入札量) か。
 *
 * MATCHED 以外は常に false (約定していない札に全量も部分もない)。 約定量が無い
 * MATCHED も false = 全約定 として扱う: 部分と断定する根拠が無く、 AC-BSP-047 に
 * 「約定量不明」 区分は無い。
 *
 * 約定量ゼロの MATCHED は逆に true = 部分約定 になる。 AC-BSP-047 が「約定量ゼロ」
 * を 未約定 と定義しているのとはズレるが、 本関数は 2 値述語なので 未約定 を返せず、
 * 3 値化 (`composeIntradayTableStatus` 同型の合成) は残り 8 区分の実装とまとめて
 * 行う話になる。 欠損を 全約定 / ゼロを 部分約定 に倒す非対称は、 backend が
 * 「約定量が欠損 **または** ゼロ」 の MATCHED 行をまとめて捨てる
 * (`bid_match_sync.go` の AC-BSP-047 ガード) ためどちらも実データでは出ない。
 * 現在の分岐は下の test で固定してある。
 *
 * 比較は「厳密に小さい」 だけで行い同値判定はしない (0.1 MW 刻みの小数を浮動小数の
 * 等値比較にかけない)。
 */
export const isSpotPartialMatched = (row: SpotBidMatchQuantities): boolean => {
  if (row.bidStatus !== BiddingSpotBidStatus.MATCHED) return false
  if (row.execVolumeMwh === undefined) return false
  const bidVolume = Number(row.volumeMwh)
  const matchedVolume = Number(row.execVolumeMwh)
  if (!Number.isFinite(bidVolume) || !Number.isFinite(matchedVolume)) {
    return false
  }
  return matchedVolume < bidVolume
}

/** 0.1 MW 刻みの数量文字列を 0.1 MW 単位の整数にする (数値でなければ undefined)。 */
const toTenthsOfMw = (volume: string): number | undefined => {
  const n = Number(volume)
  return Number.isFinite(n) ? Math.round(n * 10) : undefined
}

/**
 * 残数量 (MW) (AC-BSP-002「入札数量 − 約定数量。 約定に至らなかった量。 全約定は 0、
 * 取消・失効は「—」 表示」)。
 *
 * 値を持つのは約定結果が出た札 (全約定 / 部分約定 / 未約定) だけ。 それ以外
 * (取消・失効、 約定結果が届いていない約定待ち・送信中〈AC-BSP-101「行単位の残数量は
 * 「—」 表示のまま」〉、 締切前の札) は undefined (= 「—」)。 区分はステータス列と同じ
 * 値を呼び出し側から受け、 列ごとに導き直さない。
 *
 * 0.1 MW 刻みの小数を浮動小数のまま引くと 3.1999… のような値になるので、 0.1 MW 単位の
 * 整数で引いてから戻す。
 */
export const spotBidRemainingVolumeMw = (
  row: Pick<SpotBidRow, 'volumeMwh' | 'execVolumeMwh'>,
  category: SpotBidStatusCategory,
): number | undefined => {
  if (category === 'allMatched') return 0
  if (category !== 'partialMatched' && category !== 'unmatched') {
    return undefined
  }
  const bid = toTenthsOfMw(row.volumeMwh)
  const matched =
    row.execVolumeMwh !== undefined ? toTenthsOfMw(row.execVolumeMwh) : 0
  if (bid === undefined || matched === undefined) return undefined
  return (bid - matched) / 10
}
